import { Framework } from '@/framework/instance';
import { BackgroundLayer } from '@/layers/background-layer';
import { ChartLayer } from '@/layers/chart-layer';
import { type GroupLayer } from '@/layers/group-layer';
import { type ImageLayer } from '@/layers/image-layer';
import { Layer } from '@/layers/layer';
import { TextLayer } from '@/layers/text-layer';
import { IPoint } from '@/version-types';
import { type ClipboardPlugin } from '../clipboard';
import { type TextEditPlugin } from '../text-edit';

export class ContextMenuPluginDependency extends Framework {
    private tryGetClipboard() {
        const clipboard = this.framework.getInstance<ClipboardPlugin>('ClipboardPlugin');
        if (!clipboard) {
            throw new Error('ClipboardPlugin is not found');
        }
        return clipboard;
    }

    onCopy() {
        this.tryGetClipboard().copy();
    }
    onCut() {
        this.tryGetClipboard().cut();
    }
    onPaste(point: IPoint) {
        this.tryGetClipboard().paste(null, point);
    }
    getClipboardData() {
        return this.tryGetClipboard().getClipboardData(null);
    }

    private tryGetGroupLayer() {
        const groupLayer = this.framework.getInstance<GroupLayer>('GroupLayer');
        if (!groupLayer) {
            throw new Error('GroupLayer is not found');
        }
        return groupLayer;
    }

    pack(layerId: string) {
        this.tryGetGroupLayer().pack(layerId);
    }
    unpack(layerId: string) {
        this.tryGetGroupLayer().unpack(layerId);
    }
    createGroupAndActive(childIds: string[]) {
        this.tryGetGroupLayer().createGroupAndActive(childIds);
    }

    private tryGetTextEdit() {
        const textEdit = this.framework.getInstance<TextEditPlugin>('TextEditPlugin');
        if (!textEdit) {
            throw new Error('TextEditPlugin is not found');
        }
        return textEdit;
    }
    enterEditing(layerId: string) {
        this.tryGetTextEdit().enterEditing(layerId);
    }

    private async tryExitTextEditing(layerId: string) {
        const editingLayer = this.tryGetTextEdit().getEditingLayer();
        if (!editingLayer || editingLayer.id !== layerId) return;
        return this.tryGetTextEdit().exitEditing();
    }
    flipX(id: string) {
        this.tryExitTextEditing(id).then(() => this.framework.getInstance(Layer).flipX(id));
    }
    flipY(id: string) {
        this.tryExitTextEditing(id).then(() => this.framework.getInstance(Layer).flipY(id));
    }

    private tryGetImageLayer() {
        const imageLayer = this.framework.getInstance<ImageLayer>('ImageLayer');
        if (!imageLayer) {
            throw new Error('ImageLayer is not found');
        }
        return imageLayer;
    }
    convertImageToBackground(layerId: string, deleteImage: boolean, materialIds?: Record<string, string[]>) {
        this.tryGetImageLayer().convertImageToBackground(layerId, deleteImage, materialIds);
    }
    copyImageEffectData(layerId: string, tip?: string) {
        this.tryGetImageLayer().extractEffectData(layerId);
        this.tryGetImageLayer().setImageBrush(layerId, tip);
    }

    private tryGetChartLayer() {
        const chartLayer = this.framework.getInstance<ChartLayer>('ChartLayer');
        if (!chartLayer) {
            throw new Error('ChartLayer is not found');
        }
        return chartLayer;
    }
    openChartEditor(layerId: string) {
        this.tryGetChartLayer().openChartEditor(layerId);
    }

    private tryGetBackgroundLayer() {
        const backgroundLayer = this.framework.getInstance<BackgroundLayer>('BackgroundLayer');
        if (!backgroundLayer) {
            throw new Error('BackgroundLayer is not found');
        }
        return backgroundLayer;
    }
    convertBackgroundToImage() {
        this.tryGetBackgroundLayer().convertBackgroundToImage();
    }

    private tryGetTextLayer() {
        const textLayer = this.framework.getInstance<TextLayer>('TextLayer');
        if (!textLayer) {
            throw new Error('TextLayer is not found');
        }
        return textLayer;
    }
    extractFormatBrushData(layerId: string, tip?: string) {
        this.tryGetTextLayer().extractFormatBrushData(layerId);
        this.tryGetTextLayer().setTextBrush(
            layerId,
            (layerId) => {
                this.tryGetTextLayer().applyFormatBrush(layerId);
            },
            tip,
        );
    }
}
